import React from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import Sparkline from "../allocation/components/Sparkline";

// Calm by default: colour only appears on exceptions (attention/critical).
const HEADLINE = {
  ok: "text-text",
  attention: "text-warning",
  critical: "text-danger",
  neutral: "text-text-2",
};
// Border stays neutral for every tone — a coloured border reads like a
// "selected" state. Tone is cued by the status dot + value-text colour instead.
const BORDER_NEUTRAL = "border-card-bd";
const DOT = {
  ok: "bg-success",
  attention: "bg-warning",
  critical: "bg-danger",
  neutral: "bg-text-3",
};

// ControlKpiTile renders one status-aware KPI; clicking drills to its page.
const ControlKpiTile = ({ tile }) => {
  const tone = tile.tone || "neutral";
  const muted = !!tile.muted;
  const navigate = useNavigate();
  return (
    <Link
      to={tile.href}
      className={`flex min-w-[150px] flex-col rounded-card border px-4 py-3 hover:border-border ${BORDER_NEUTRAL} ${
        muted ? "bg-surface-2 opacity-60" : "bg-card"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${DOT[tone]}`} />
        <span className="text-xs font-medium uppercase tracking-wide text-text-2">{tile.label}</span>
        {tile.info ? (
          <span
            // text-xs/leading-none so the info glyph doesn't inflate the label row height (which
            // would push this tile's value down out of line with the other tiles' values).
            className="ml-auto cursor-help text-xs leading-none text-text-3"
            data-tooltip-id="instantTooltip"
            data-tooltip-content={tile.info}
            aria-label={tile.info}
          >
            ⓘ
          </span>
        ) : null}
      </div>
      <div className={`mt-1 ${muted ? "text-base font-medium" : "text-xl font-semibold"} tabular-nums ${HEADLINE[tone]}`}>{tile.value}</div>
      {tile.why && <div className="mt-1 text-xs leading-snug text-text-2">{tile.why}</div>}
      {Array.isArray(tile.spark) && tile.spark.length >= 2 && (
        <div className="mt-2" title="Trend over the last 90 days">
          <Sparkline data={tile.spark} goodDirection={tile.sparkGoodDirection || "up"} />
        </div>
      )}
      {tile.cta && (
        <span className="mt-2 inline-flex items-center rounded-ctrl bg-primary px-2.5 py-1 text-xs font-medium text-white">
          {tile.cta}
        </span>
      )}
      {tile.link && (
        <div className="mt-auto pt-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(tile.link.to);
            }}
            className="text-xs font-medium text-primary hover:text-primary-hover"
          >
            {tile.link.label}
          </button>
        </div>
      )}
    </Link>
  );
};

ControlKpiTile.propTypes = {
  tile: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    why: PropTypes.string,
    tone: PropTypes.oneOf(["ok", "attention", "critical", "neutral"]),
    href: PropTypes.string.isRequired,
    muted: PropTypes.bool,
    info: PropTypes.string,
    cta: PropTypes.string,
    spark: PropTypes.arrayOf(PropTypes.number),
    sparkGoodDirection: PropTypes.string,
    link: PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default ControlKpiTile;
